/**
 * Conditionally starts the MSW browser Service Worker before React renders.
 *
 * @module mocks/enableMocking
 *
 * @remarks
 * Call this function in `main.tsx` before `createRoot(…).render(…)`:
 *
 * ```typescript
 * enableMocking().then(() => {
 *   createRoot(document.getElementById("root")!).render(<App />);
 * });
 * ```
 *
 * ## When is MSW active?
 *
 * | Environment | `VITE_MSW_ENABLED` | MSW active? |
 * |---|---|---|
 * | Native dev (`rushx dev`) | Set to `"true"` in `.env.local` | Yes |
 * | Docker compose | Set via `MSW_ENABLED=true make up` | Yes |
 * | Vitest unit tests | N/A — server always on in `vitest.setup.ts` | Yes |
 * | CI / integration tests | Not set (defaults `"false"`) | No |
 * | Production | Not set | No |
 *
 * The dynamic `import("./browser")` ensures the Service Worker bundle is only
 * loaded — and registered — when the flag is active. Vite tree-shakes it out
 * of production builds.
 *
 * ## Mocking only some features
 *
 * Once MSW is active, `VITE_MSW_MOCKED_FEATURES` (optional, comma-separated —
 * see `mocks/handlers/index.ts`) narrows MSW to only the listed feature keys,
 * so you can mix mocked and real endpoints (e.g. a real Cases backend with a
 * still-mocked Leave Assessment). Unset mocks every feature; set but empty
 * mocks none (everything falls through to the real backend).
 */
export async function enableMocking(): Promise<void> {
  if (import.meta.env.VITE_MSW_ENABLED !== "true") return;

  const { worker } = await import("./browser");
  await worker.start({
    // Let unregistered requests reach the real network silently.
    onUnhandledRequest: "bypass",
  });
}
